import React, { useEffect, useRef } from "react";
import { FaShieldAlt, FaGavel, FaEnvelope, FaPhoneAlt } from "react-icons/fa";

const Committee = () => {
  const sectionRef = useRef(null);
  const committeesRef = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-fade-up");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    committeesRef.current.forEach((card) => {
      if (card) observer.observe(card);
    });

    return () => observer.disconnect();
  }, []);

  const committees = [
    {
      id: 1,
      title: "Gender Harassment Committee",
      icon: <FaShieldAlt className="text-3xl text-pink-600" />,
      description: "The committee ensures a safe and respectful environment for all students and staff, addressing any complaints of gender-based harassment promptly and confidentially.",
      members: [
        { role: "Chairperson", name: "_______________", designation: "_______________", contact: "_______________", phone: "_______________" },
        { role: "Member Secretary", name: "_______________", designation: "_______________", contact: "_______________", phone: "_______________" },
        { role: "Member", name: "_______________", designation: "_______________", contact: "_______________", phone: "_______________" },
        { role: "Member", name: "_______________", designation: "_______________", contact: "_______________", phone: "_______________" },
        { role: "External Member", name: "_______________", designation: "_______________", contact: "_______________", phone: "_______________" }
      ]
    },
    {
      id: 2,
      title: "Anti-Ragging Committee",
      icon: <FaGavel className="text-3xl text-red-600" />,
      description: "The committee strictly prohibits any form of ragging in the campus and takes immediate action against offenders as per UGC guidelines and the Prohibition of Ragging Act.",
      members: [
        { role: "Chairperson", name: "_______________", designation: "_______________", contact: "_______________", phone: "_______________" },
        { role: "Convener", name: "_______________", designation: "_______________", contact: "_______________", phone: "_______________" },
        { role: "Member", name: "_______________", designation: "_______________", contact: "_______________", phone: "_______________" },
        { role: "Member", name: "_______________", designation: "_______________", contact: "_______________", phone: "_______________" },
        { role: "Student Representative", name: "_______________", designation: "_______________", contact: "_______________", phone: "_______________" },
        { role: "External Member", name: "_______________", designation: "_______________", contact: "_______________", phone: "_______________" }
      ]
    }
  ];

  return (
    <section className="py-8 px-4 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-indigo-500/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="w-full px-4 md:px-8" ref={sectionRef}>
        <div className="text-center mb-8 animate-fade-in">
          <h2 className="text-3xl md:text-4xl font-bold mb-3">
            <span className="bg-gradient-to-r from-blue-600 via-indigo-500 to-blue-600 bg-clip-text text-transparent animate-gradient">
              Committees
            </span>
          </h2>
          <p className="text-gray-600 text-base max-w-2xl mx-auto">
            Ensuring safety, discipline, and equality at RDS College of Pharmacy
          </p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {committees.map((committee, idx) => (
            <div
              key={committee.id}
              ref={el => (committeesRef.current[idx] = el)}
              className="group opacity-0 transition-all duration-700 transform translate-y-8 hover:-translate-y-1 hover:shadow-xl rounded-2xl shadow-md overflow-hidden bg-gradient-to-br from-blue-50 via-white to-indigo-50 border-t-4 border-blue-500"
            >
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4 pb-3 border-b border-gray-200">
                  <div className="p-2 bg-white rounded-full shadow-sm">
                    {committee.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-800">
                    {committee.title}
                  </h3>
                </div>
                <p className="text-gray-600 text-sm mb-6 leading-relaxed">
                  {committee.description}
                </p>

                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white rounded-lg shadow-sm border border-gray-200 text-sm">
                    <thead className="bg-blue-50 text-gray-700">
                      <tr>
                        <th className="px-3 py-2 text-left border-b">Role</th>
                        <th className="px-3 py-2 text-left border-b">Name</th>
                        <th className="px-3 py-2 text-left border-b hidden md:table-cell">Designation</th>
                        <th className="px-3 py-2 text-left border-b hidden lg:table-cell">Contact</th>
                      </tr>
                    </thead>
                    <tbody>
                      {committee.members.map((member, i) => (
                        <tr key={i} className="border-b hover:bg-gray-50">
                          <td className="px-3 py-2 font-medium text-gray-700">{member.role}</td>
                          <td className="px-3 py-2 text-gray-600">{member.name}</td>
                          <td className="px-3 py-2 text-gray-600 hidden md:table-cell">{member.designation}</td>
                          <td className="px-3 py-2 text-gray-600 hidden lg:table-cell">
                            <div className="flex flex-col text-xs">
                              <span className="flex items-center gap-1">
                                <FaEnvelope className="text-blue-500 text-xs" />
                                <span className="truncate max-w-[150px]">{member.contact}</span>
                              </span>
                              <span className="flex items-center gap-1 mt-1">
                                <FaPhoneAlt className="text-blue-500 text-xs" />
                                <span>{member.phone}</span>
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-4 lg:hidden">
                  {committee.members.map((member, i) => (
                    <div key={i} className="text-xs border-t border-gray-100 pt-2 mt-2 first:border-t-0 first:pt-0 first:mt-0">
                      <span className="font-semibold">{member.role}:</span> {member.name} ({member.designation})<br />
                      <span className="text-blue-600">{member.contact}</span> | <span className="text-blue-600">{member.phone}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 pt-4 border-t border-gray-200 text-xs text-gray-500 bg-blue-50/50 p-3 rounded-lg">
                  <p className="flex items-center gap-2">
                    <FaShieldAlt className="text-blue-600" />
                    <span>
                      <strong>Anti-Ragging Helpline:</strong> 1800-180-5522 | <strong>Women Helpline:</strong> 1091
                    </span>
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <style jsx>{`
        @keyframes fade-up {
          from {
            opacity: 0;
            transform: translateY(2rem);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-up {
          animation: fade-up 0.6s ease-out forwards;
        }
        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        .animate-fade-in {
          animation: fade-in 0.8s ease-out forwards;
        }
        @keyframes gradient {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }
        .animate-gradient {
          background-size: 200% auto;
          animation: gradient 3s ease infinite;
        }
      `}</style>
    </section>
  );
};

export default Committee;